import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startingDay = new Date(year, month, 1).getDay();
  return { daysInMonth, startingDay };
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const BookingStepDateTime = ({
  formData, setFormData, setStep,
  availableSlots, currentMonth, setCurrentMonth,
  selectedDate, setSelectedDate
}) => {
  const isDateDisabled = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateSelect = (day) => {
    if (!isDateDisabled(day)) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      setSelectedDate(date);
      setFormData({ ...formData, date: formatDate(date), time: '' });
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const headers = dayNames.map((day) => (
      <div key={day} className="text-center text-xs font-medium text-stone-500 py-2">
        {day}
      </div>
    ));

    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isDisabled = isDateDisabled(day);
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === currentMonth.getMonth();
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={isDisabled}
          data-testid={`calendar-day-${day}`}
          className={`p-2 text-sm rounded-lg transition-all ${
            isDisabled 
              ? 'text-stone-300 cursor-not-allowed' 
              : isSelected 
                ? 'bg-luxury-green text-white' 
                : 'hover:bg-sage-100 text-stone-700'
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="font-medium">
            {currentMonth.toLocaleDateString('en-IE', { month: 'long', year: 'numeric' })}
          </span>
          <button 
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {headers}
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-base sm:text-lg font-semibold text-stone-800">Select Date & Time</h3>
      
      <div className="bg-stone-50 rounded-xl p-3 sm:p-4">
        {renderCalendar()}
      </div>

      {formData.date && (
        <div>
          <p className="text-sm font-medium text-stone-600 mb-3">Available Times</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[150px] sm:max-h-[200px] overflow-y-auto">
            {availableSlots.map((slot) => (
              <button
                key={slot.time}
                data-testid={`time-slot-${slot.time}`}
                onClick={() => {
                  if (slot.available) {
                    setFormData({ ...formData, time: slot.time });
                  }
                }}
                disabled={!slot.available}
                className={`py-2 px-2 sm:px-3 text-xs sm:text-sm rounded-lg transition-all ${
                  !slot.available 
                    ? 'bg-stone-100 text-stone-300 cursor-not-allowed line-through' 
                    : formData.time === slot.time 
                      ? 'bg-luxury-green text-white' 
                      : 'bg-white border border-stone-200 hover:border-sage-400 text-stone-700'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>
        </div>
      )}

      {formData.date && formData.time && (
        <Button 
          onClick={() => setStep(4)} 
          className="w-full bg-luxury-green hover:bg-luxury-green/90 text-white rounded-full"
        >
          Continue
        </Button>
      )}
    </div>
  );
};

export default BookingStepDateTime;
